import type { InitialMessage } from '@databox/types/domains/ai/chat';
import { ChatMessageType } from '@databox/types/domains/ai/chat.enums';
import type { ContextObjectBase } from '@databox/types/domains/ai/context';

import { b64_to_utf8, utf8_to_b64 } from 'webapp/utils/common';

import type { AiChatSetupOptions } from 'webapp/controllers/genie-thread-base';

const VALID_ROLES = new Set<string>([ChatMessageType.HUMAN, ChatMessageType.AI]);

function isValidMessage(msg: unknown): msg is InitialMessage {
	if (!msg || typeof msg !== 'object') {
		return false;
	}

	const message = msg as Record<string, unknown>;
	return typeof message.content === 'string' && VALID_ROLES.has(message.role as string);
}

function isValidContextItem(item: unknown): item is ContextObjectBase {
	if (!item || typeof item !== 'object') {
		return false;
	}

	const contextItem = item as Record<string, unknown>;
	return (
		typeof contextItem.objectType === 'string' &&
		contextItem.objectTypeId !== null &&
		contextItem.objectTypeId !== undefined
	);
}

export function decodeAiChatPayload(encoded: string): AiChatSetupOptions | null {
	try {
		// validated field by field below
		const json = JSON.parse(b64_to_utf8(encoded)) as {
			messages?: unknown;
			context?: unknown;
			prompt?: unknown;
		} | null;

		if (!json || typeof json !== 'object') {
			return null;
		}

		const messages = Array.isArray(json.messages) ? json.messages.filter(isValidMessage) : [];
		const context = Array.isArray(json.context) ? json.context.filter(isValidContextItem) : [];
		const prompt =
			typeof json.prompt === 'string' && json.prompt.trim() ? json.prompt : undefined;

		// A prompt-only payload is valid — without this it would be discarded as empty.
		if (!messages.length && !context.length && !prompt) {
			return null;
		}

		return {
			initialMessages: messages,
			contextItems: context,
			initialPrompt: prompt,
		};
	} catch {
		return null;
	}
}

export function encodeAiChatPrompt(prompt: string): string {
	return utf8_to_b64(JSON.stringify({ prompt }));
}

// Unlike encodeAiChatPrompt (seeds the composer only), this submits the prompt as an initial
// human message — the two are one character apart at the call site, so the distinction is
// otherwise invisible.
export function encodeAiChatMessage(prompt: string): string {
	return utf8_to_b64(
		JSON.stringify({ messages: [{ role: ChatMessageType.HUMAN, content: prompt }] }),
	);
}
